/**
 * Elastic-scroll lock for scroll areas that actually overflow.
 *
 * macOS bounces a scroll area past its ends; `overscroll-behavior: none`
 * stops that, but it also stops the scroll from passing on to the parent.
 * On a scroll container that does NOT overflow on an axis (a table wrapper
 * whose rows fit, an `overflow-y-auto` box that only sometimes scrolls) that
 * second effect is a trap: the wheel over it scrolls nothing at all, and the
 * page only moves with the pointer outside it. CSS cannot tell whether a box
 * overflows, so this does: whenever the pointer moves over the app (and on
 * each wheel, for content that changed under a still pointer) it marks the
 * scroll containers under it with the axes that overflow right now, and
 * styles/no-elastic-scroll.css turns the bounce off for exactly those axes.
 */

const ATTRIBUTE = 'data-overscroll-lock'
const SCROLLS = /(auto|scroll)/
/** Deep enough for any layout; a guard against pathological trees. */
const MAX_DEPTH = 60

function lockedAxes(element: HTMLElement): string {
  const style = getComputedStyle(element)
  const axes: string[] = []
  if (
    SCROLLS.test(style.overflowX) &&
    element.scrollWidth > element.clientWidth + 1
  ) {
    axes.push('x')
  }
  if (
    SCROLLS.test(style.overflowY) &&
    element.scrollHeight > element.clientHeight + 1
  ) {
    axes.push('y')
  }
  return axes.join(' ')
}

/** Mark the scroll containers from `start` up to the page root. */
export function refreshOverscrollLocks(start: EventTarget | null): void {
  let node = start instanceof Element ? start : null
  const changes: Array<[HTMLElement, string]> = []
  for (
    let depth = 0;
    node && node !== document.documentElement && depth < MAX_DEPTH;
    depth += 1
  ) {
    if (node instanceof HTMLElement) {
      const axes = lockedAxes(node)
      if ((node.getAttribute(ATTRIBUTE) ?? '') !== axes) {
        changes.push([node, axes])
      }
    }
    node = node.parentElement
  }
  // Finish all geometry reads before any style invalidation. A nested scroll
  // area must not force the next ancestor's measurement to recalculate layout.
  for (const [element, axes] of changes) {
    if (axes) element.setAttribute(ATTRIBUTE, axes)
    else element.removeAttribute(ATTRIBUTE)
  }
}

let isInitialized = false

export function initOverscrollLock(): void {
  if (isInitialized || typeof document === 'undefined') return
  isInitialized = true
  const handle = (event: Event) => refreshOverscrollLocks(event.target)
  // Keep the first gesture's locks synchronous. pointerover lands before the wheel that
  // follows it, so the first gesture already sees the right lock.
  document.addEventListener('pointerover', handle, {
    capture: true,
    passive: true,
  })
  document.addEventListener('wheel', handle, { capture: true, passive: true })
}
